/**
 * OpportunitiesCenter — dismissable / savable / completable opportunity cards.
 * Reads /api/opportunities, mutates via POST /opportunities/{id}/{action}.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Target, CheckCircle2, BookmarkPlus, X, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";
import { formatINR as money } from "../../lib/format";

const TYPE_META = {
  revenue:     { color: "text-orange-400",  bg: "bg-orange-500/10 border-orange-500/30",  label: "Revenue" },
  growth:      { color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "Growth" },
  product:     { color: "text-sky-400",     bg: "bg-sky-500/10 border-sky-500/30",         label: "Product" },
  category:    { color: "text-violet-400",  bg: "bg-violet-500/10 border-violet-500/30",   label: "Category" },
  marketplace: { color: "text-cyan-400",    bg: "bg-cyan-500/10 border-cyan-500/30",       label: "Marketplace" },
  operational: { color: "text-red-400",     bg: "bg-red-500/10 border-red-500/30",         label: "Operational" },
};

const PRIORITY_DOT = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-zinc-500" };

export default function OpportunitiesCenter({ opportunities, onChange }) {
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const filtered = opportunities.filter((o) => (filter === "all" ? true : filter === "saved" ? o.status === "saved" : o.type === filter));

  const act = async (id, action) => {
    try {
      await api.post(`/opportunities/${id}/${action}`);
      toast.success(`Opportunity ${action === "reopen" ? "reopened" : action + "d"}`);
      onChange?.();
    } catch (e) {
      toast.error("Failed. Please try again.");
    }
  };

  return (
    <section data-testid="opps-center">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2 shrink-0">
          <Target className="h-3.5 w-3.5" /> Opportunities Center
        </h2>
        <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1 text-xs overflow-x-auto min-w-0 max-w-full">
          {["all", "revenue", "growth", "product", "category", "marketplace", "operational", "saved"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`opps-filter-${k}`}
              className={`px-2 py-1 rounded transition-colors capitalize ${filter === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-8 text-center" data-testid="opps-empty">
          <Target className="mx-auto h-6 w-6 text-zinc-600" />
          <p className="mt-3 text-sm text-zinc-400">No opportunities right now — your business is running clean, or you need more data.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {filtered.map((o) => {
            const meta = TYPE_META[o.type] || TYPE_META.revenue;
            const isOpen = expanded[o.opportunity_id];
            return (
              <article
                key={o.opportunity_id}
                data-testid={`opp-card-${o.opportunity_id}`}
                className={`rounded-2xl border p-4 transition-all ${o.status === "completed" ? "opacity-60" : ""} ${meta.bg}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[o.priority] || PRIORITY_DOT.low}`} />
                      <span className={`text-[10px] uppercase tracking-[0.14em] font-bold ${meta.color}`}>{meta.label}</span>
                      <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">· {o.confidence}% confidence</span>
                      {o.status !== "open" && (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-bold ml-1">· {o.status}</span>
                      )}
                    </div>
                    <h3 className="mt-1.5 text-sm font-medium text-zinc-100">{o.title}</h3>
                    <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{o.description}</p>
                    {o.estimated_revenue_impact > 0 && (
                      <p className="mt-2 text-xs text-zinc-300">
                        <span className="text-zinc-500">Estimated impact:</span>{" "}
                        <span className="text-zinc-100 tabular-nums">{money(o.estimated_revenue_impact)}</span>
                      </p>
                    )}
                    <p className="mt-2 text-xs text-zinc-300">
                      <span className="text-zinc-500">Action:</span> {o.recommended_action}
                    </p>

                    {isOpen && (
                      <div className="mt-3 space-y-2 text-xs text-zinc-400 border-t border-zinc-800/60 pt-3">
                        <p><span className="text-zinc-500">Reason:</span> {o.reason}</p>
                        <p><span className="text-zinc-500">Business impact:</span> {o.business_impact}</p>
                      </div>
                    )}

                    <button
                      onClick={() => setExpanded((s) => ({ ...s, [o.opportunity_id]: !s[o.opportunity_id] }))}
                      className="mt-3 text-[10px] text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"
                      data-testid={`opp-toggle-${o.opportunity_id}`}
                    >
                      <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      {isOpen ? "Hide" : "Show"} details
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2 pt-3 border-t border-zinc-800/60">
                  {o.status === "completed" ? (
                    <button onClick={() => act(o.opportunity_id, "reopen")} className="text-[11px] text-zinc-400 hover:text-zinc-200" data-testid={`opp-${o.opportunity_id}-reopen`}>
                      Reopen
                    </button>
                  ) : (
                    <>
                      <button onClick={() => act(o.opportunity_id, "complete")} className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300" data-testid={`opp-${o.opportunity_id}-complete`}>
                        <CheckCircle2 className="h-3 w-3" /> Mark complete
                      </button>
                      <button onClick={() => act(o.opportunity_id, "save")} className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-200" data-testid={`opp-${o.opportunity_id}-save`}>
                        <BookmarkPlus className="h-3 w-3" /> Save
                      </button>
                      <button onClick={() => act(o.opportunity_id, "dismiss")} className="ml-auto inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300" data-testid={`opp-${o.opportunity_id}-dismiss`}>
                        <X className="h-3 w-3" /> Dismiss
                      </button>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
